import { useTranslation as useI18Translation } from "react-i18next"
import { useSelector } from "react-redux"
import { usePathname, useSearchParams } from "next/navigation"
import humps from "humps"
import type { i18n as I18n } from "i18next"

import type { RootState } from "@/types/redux"
import type { Translate, TranslateContext } from "@/types/util"

const generateKeyWithContext = (i18: I18n, key: string, contexts: TranslateContext): string => {
  for (const [context, value] of Object.entries(contexts)) {
    if (!value && !Number.isInteger(value)) {
      continue
    }
    const keyWithContext = key + "__" + context + "__" + value
    if (i18.exists(keyWithContext)) {
      return keyWithContext
    }
  }
  return key
}

const injectProjectName = (
  value: string,
  translation: ReturnType<typeof useI18Translation>,
  ciabName: string | undefined,
): string => {
  if (!ciabName) {
    return value
  }
  const projectNamePlaceholder = "{{projectName}}"
  if (value.includes(projectNamePlaceholder)) {
    console.log("ciabName", ciabName, translation.t(`projectName__ciab__${ciabName}`))
    const projectName = translation.t(`projectName__ciab__${ciabName}`)
    value = value.replace(projectNamePlaceholder, projectName)
  }
  return value
}

const useTranslation = () => {
  const translation = useI18Translation()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const ciabName = useSelector((s: RootState) => s.configuration?.ciabName)
  const gender = useSelector((s: RootState) => s.auth?.user?.details?.gender)

  let cohortSlug = useSelector((s: RootState) => s.auth?.user?.cohort?.slug) as string | undefined | null
  if (pathname?.toLowerCase().startsWith("/signup")) {
    cohortSlug = searchParams.get("cohort")
  }
  let fileType: string | undefined
  if (pathname?.toLowerCase().startsWith("/medical-files") && pathname.split("/")[2]) {
    fileType = humps.camelize(pathname.split("/")[2] ?? "")
  }

  const defaultContext: TranslateContext = {
    gender,
    cohortSlug,
    fileType,
    ciab: ciabName,
  }

  const t: Translate = (key, context) => {
    const contextEntries = Object.entries({
      ...defaultContext,
      ...(context || {}),
    })

    const camelizedEntries = contextEntries.map(([k, value]) => {
      if (value && typeof value === "string") {
        return [k, humps.camelize(value)] as const
      }
      return [k, value] as const
    })
    const camelizedContext = Object.fromEntries(camelizedEntries)
    const keyWithContext = generateKeyWithContext(translation.i18n, key, camelizedContext)

    let value = translation.t(keyWithContext)
    value = injectProjectName(value, translation, ciabName)
    return value
  }
  return { ...translation, t }
}

export default useTranslation
